import type { DeviceInfo } from './tipos'

/**
 * Detecta tipo de dispositivo
 */
function detectarTipoDispositivo(): 'mobile' | 'desktop' | 'tablet' {
  if (typeof window === 'undefined') return 'desktop'

  const ua = navigator.userAgent.toLowerCase()
  const width = window.innerWidth

  // Tablets primeiro
  if (
    /ipad/.test(ua) ||
    (/android/.test(ua) && !/mobile/.test(ua)) ||
    (width >= 768 && width < 1024 && 'ontouchstart' in window)
  ) {
    return 'tablet'
  }

  // Mobile
  if (
    /mobile|iphone|ipod|android.*mobile|blackberry|windows phone|opera mini|opera mobi/.test(ua) ||
    width < 768
  ) {
    return 'mobile'
  }

  return 'desktop'
}

/**
 * Detecta browser e versão
 */
function detectarBrowser(): { name: string; version: string } {
  if (typeof window === 'undefined') return { name: 'Unknown', version: '0' }

  const ua = navigator.userAgent

  // Samsung Internet (antes do Chrome pois também contém "Chrome")
  if (/samsungbrowser/i.test(ua)) {
    const match = ua.match(/samsungbrowser\/(\d+(\.\d+)?)/i)
    return { name: 'Samsung Internet', version: match ? match[1] : '0' }
  }

  // Edge (antes do Chrome pois também contém "Chrome")
  if (/edg/i.test(ua)) {
    const match = ua.match(/edg\/(\d+(\.\d+)?)/i)
    return { name: 'Edge', version: match ? match[1] : '0' }
  }

  // Opera (antes do Chrome pois também contém "Chrome")
  if (/opr|opera/i.test(ua)) {
    const match = ua.match(/(?:opr|opera)\/(\d+(\.\d+)?)/i)
    return { name: 'Opera', version: match ? match[1] : '0' }
  }

  // Chrome
  if (/chrome|chromium|crios/i.test(ua)) {
    const match = ua.match(/(?:chrome|chromium|crios)\/(\d+(\.\d+)?)/i)
    return { name: 'Chrome', version: match ? match[1] : '0' }
  }

  // Firefox
  if (/firefox|fxios/i.test(ua)) {
    const match = ua.match(/(?:firefox|fxios)\/(\d+(\.\d+)?)/i)
    return { name: 'Firefox', version: match ? match[1] : '0' }
  }

  // Safari (por último pois muitos browsers contém "Safari")
  if (/safari/i.test(ua)) {
    const match = ua.match(/version\/(\d+(\.\d+)?)/i)
    return { name: 'Safari', version: match ? match[1] : '0' }
  }

  return { name: 'Other', version: '0' }
}

/**
 * Detecta SO e versão
 */
function detectarSO(): { name: string; version: string } {
  if (typeof window === 'undefined') return { name: 'Unknown', version: '0' }

  const ua = navigator.userAgent

  if (/iphone|ipad|ipod/i.test(ua)) {
    const match = ua.match(/os (\d+[._]\d+)/i)
    return { name: 'iOS', version: match ? match[1].replace('_', '.') : '0' }
  }

  if (/android/i.test(ua)) {
    const match = ua.match(/android (\d+(\.\d+)?)/i)
    return { name: 'Android', version: match ? match[1] : '0' }
  }

  if (/windows/i.test(ua)) {
    let version = '0'
    if (/windows nt 10/i.test(ua)) version = '10'
    else if (/windows nt 11/i.test(ua)) version = '11'
    else if (/windows nt 6.3/i.test(ua)) version = '8.1'
    else if (/windows nt 6.2/i.test(ua)) version = '8'
    else if (/windows nt 6.1/i.test(ua)) version = '7'
    return { name: 'Windows', version }
  }

  if (/macintosh|mac os x/i.test(ua)) {
    const match = ua.match(/mac os x (\d+[._]\d+)/i)
    return { name: 'macOS', version: match ? match[1].replace('_', '.') : '0' }
  }

  if (/linux/i.test(ua)) {
    return { name: 'Linux', version: '' }
  }

  return { name: 'Unknown', version: '0' }
}

/**
 * Coleta informações completas do dispositivo
 */
export function getDeviceInfo(): DeviceInfo {
  if (typeof window === 'undefined') {
    return {
      type: 'desktop',
      browser: 'Unknown',
      browserVersion: '0',
      os: 'Unknown',
      osVersion: '0',
      screen: { width: 0, height: 0 },
      viewport: { width: 0, height: 0 },
      touchEnabled: false,
    }
  }

  const browser = detectarBrowser()
  const os = detectarSO()

  return {
    type: detectarTipoDispositivo(),
    browser: browser.name,
    browserVersion: browser.version,
    os: os.name,
    osVersion: os.version,
    screen: {
      width: window.screen.width,
      height: window.screen.height,
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    touchEnabled: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
  }
}
